import { DiffRow, Badge, Button } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

// A stacked list of small before/after changes - the surface DiffRow is built
// for (version side-rail, counter-redline review). The consumer passes already-
// segmented strings; DiffRow is not a word-level differ.
const CHANGES: { removed: string; added: string; meta: string }[] = [
  {
    removed: "within thirty (30) days",
    added: "within forty-five (45) days",
    meta: "Section 4.2 - Payment terms",
  },
  {
    removed: "governed by the laws of New York",
    added: "governed by the laws of Delaware",
    meta: "Section 11.1 - Governing law",
  },
  {
    removed: "may terminate this agreement",
    added: "may terminate this agreement upon 30 days written notice",
    meta: "Section 9.3 - Termination",
  },
];

export function DiffRowDemo() {
  return (
    <Section
      id="diff-row"
      title="DiffRow"
      description="Single-row diff view for inline change lists: removed text on the left, added text on the right, an optional meta line (clause anchor / context), and an optional verdict slot (status badge or accept / reject controls) on the right edge. Each cell renders through RedlineSpan so the ins / del coloring matches inline redlines. Density toggles between comfortable and compact for dense review lists."
    >
      <VariantBlock label="Default + density" caption="comfortable (left/top) vs compact (tighter padding + xs text)">
        <div className="grid w-[560px] max-w-full gap-3">
          <DiffRow removed="within thirty (30) days" added="within forty-five (45) days" />
          <DiffRow density="compact" removed="within thirty (30) days" added="within forty-five (45) days" />
        </div>
      </VariantBlock>

      <VariantBlock label="Meta + verdict" caption="a clause anchor in the footer, with a status badge or accept / reject controls on the right">
        <div className="grid w-[560px] max-w-full gap-3">
          <DiffRow
            removed="within thirty (30) days"
            added="within forty-five (45) days"
            meta="Section 4.2 - Payment terms"
            verdict={<Badge variant="warning">Needs review</Badge>}
          />
          <DiffRow
            removed="may terminate this agreement"
            added="may terminate this agreement upon 30 days written notice"
            meta="Counter-redline from opposing counsel"
            verdict={
              <span className="flex gap-2">
                <Button size="sm" variant="ghost">
                  Reject
                </Button>
                <Button size="sm">Accept</Button>
              </span>
            }
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Multi-line diff" caption="a stacked list of small changes - the review side-rail surface">
        <div className="flex w-[560px] max-w-full flex-col gap-2">
          {CHANGES.map((c) => (
            <DiffRow key={c.meta} removed={c.removed} added={c.added} meta={c.meta} />
          ))}
        </div>
      </VariantBlock>

      <VariantBlock label="Interactive" caption="an onClick makes the whole row a keyboard-operable button (scroll-to-clause)">
        <div className="w-[560px] max-w-full">
          <DiffRow
            removed="old wording"
            added="new wording"
            meta="Click or press Enter / Space to jump to the clause"
            onClick={() => {
              // eslint-disable-next-line no-console
              console.log("scroll to clause");
            }}
          />
        </div>
      </VariantBlock>
    </Section>
  );
}
